import { action, computed, observable, makeObservable } from "mobx";
import { MrbBaseViewStore } from "mrb/core";
import { MrbSelectStore } from "mrb/components/select/stores";
import { localizationService } from "common/localization";
import { map, sumBy, some, uniqBy, isNil } from "lodash";

class ProducerRow {
    @observable producerId = null;
    @observable revenuePercentage = 0;
    @observable isActive = true;
    producerSelectStore = null;

    constructor(findProducers, existingProducer = null) {
        makeObservable(this);

        this.producerSelectStore = new MrbSelectStore({
            isSearchable: true,
            virtualized: false,
            initFetch: false,
            textKey: "name",
            valueKey: "id",
            isClearable: true,
            actions: {
                find: (searchTerm) => findProducers(searchTerm),
                onChange: action((selectedOption) => {
                    this.producerId = selectedOption ? selectedOption.value : null;
                }),
            },
        });

        if (existingProducer) {
            this.producerId = existingProducer.producerId;
            this.revenuePercentage = existingProducer.revenuePercentage;
            this.isActive = existingProducer.isActive;
            if (existingProducer.producer) {
                this.producerSelectStore.setSelectedItem(existingProducer.producer);
            }
        }
    }
}

class NakiNigunAlbumTrackProducerEditViewStore extends MrbBaseViewStore {
    @observable producerRows = [];
    @observable isSaving = false;

    @computed get totalRevenue() {
        return sumBy(this.producerRows, (row) => row.revenuePercentage);
    }

    @computed get isValid() {
        if (this.producerRows.length === 0) {
            return true;
        }
        if (some(this.producerRows, (row) => isNil(row.producerId))) {
            return false;
        }
        if (some(this.producerRows, (row) => row.revenuePercentage < 0.01)) {
            return false;
        }
        const uniqueProducers = uniqBy(this.producerRows, (row) => row.producerId);
        if (uniqueProducers.length !== this.producerRows.length) {
            return false;
        }
        return Math.round(this.totalRevenue * 100) / 100 === 100;
    }

    constructor(rootStore, { nakiNigunAlbumTrackListViewStore, trackId, producers, trackTitle, closeModal }) {
        super(rootStore);
        makeObservable(this);
        this.nakiNigunAlbumTrackListViewStore = nakiNigunAlbumTrackListViewStore;
        this.routeStore = nakiNigunAlbumTrackListViewStore.routeStore;
        this.trackId = trackId;
        this.trackTitle = trackTitle;
        this.closeModal = closeModal;

        this.findProducers = this.routeStore.findProducers.bind(this.routeStore);

        if (producers && producers.length > 0) {
            this.producerRows = producers.map((p) => new ProducerRow(this.findProducers, p));
        }
    }

    @action.bound
    addRow() {
        this.producerRows.push(new ProducerRow(this.findProducers));
    }

    @action.bound
    removeRow(index) {
        this.producerRows.splice(index, 1);
    }

    @action.bound
    setRowRevenue(index, value) {
        const parsed = parseFloat(value);
        this.producerRows[index].revenuePercentage = isNaN(parsed)
            ? 0
            : Math.round(Math.max(0, Math.min(100, parsed)) * 100) / 100;
    }

    @action.bound
    async save() {
        if (!this.isValid) {
            return;
        }

        this.isSaving = true;
        try {
            const resource = map(this.producerRows, (row) => ({
                isActive: row.isActive,
                producerId: row.producerId,
                revenuePercentage: row.revenuePercentage,
            }));

            await this.routeStore.updateTrackProducers(this.trackId, resource);

            const updatedProducers = map(this.producerRows, (row) => ({
                isActive: row.isActive,
                nakiNigunAlbumTrackId: this.trackId,
                producerId: row.producerId,
                producer: row.producerSelectStore.selectedItem,
                revenuePercentage: row.revenuePercentage,
            }));

            this.nakiNigunAlbumTrackListViewStore.updateTrackProducersLocally(this.trackId, updatedProducers);
            this.rootStore.notificationStore.success(
                localizationService.t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.SUCCESS.UPDATE")
            );
            this.closeModal();
        } catch (err) {
            this.rootStore.notificationStore.error(
                localizationService.t("ADMINISTRATION.NAKI_NIGUN.SONGS.PRODUCERS.ERROR.UPDATE_GENERIC"),
                err
            );
        } finally {
            this.isSaving = false;
        }
    }

    destroy() {
        super.destroy();
    }
}

export default NakiNigunAlbumTrackProducerEditViewStore;
